import * as Status from '../../../constants/status';
import * as Role from '../../../constants/role';
import { isRoleAuditoriaNsir } from '../../../helpers/auditoriaNsirPermission';
import { INSPECTION_FIELD_STATUSES } from '../../../helpers/digitacaoAntecipada';
import { isUsuarioUsoMuto } from '../../../utils';

export const NSIR_PERSONA = {
  USO_MUTUO: 'USO_MUTUO',
  AUDITORIA_NSIR: 'AUDITORIA_NSIR',
  DIGITADOR: 'DIGITADOR',
  INSPETOR: 'INSPETOR',
  CROQUI: 'CROQUI',
  ADMIN: 'ADMIN',
  DEFAULT: 'DEFAULT',
};

const INSPECAO_ATIVA = [
  Status.EM_INSPECAO,
  Status.INSPECAO_EM_ENVIO,
  Status.INSPECAO_ENVIADA_PARCIAL,
  Status.EM_ANALISE,
];

const ERRO_INSPECAO = [
  Status.INSPECAO_SYNC_ERROR,
  Status.ERRO_AO_ENVIAR_INSPECAO,
];

const INSPECAO_COMPLETA = [
  Status.AGUARDANDO_INSPECAO,
  ...INSPECAO_ATIVA,
  Status.INSPECAO_FINALIZADA,
  Status.INSPECAO_ENVIADA_COM_SUCESSO,
];

const DIGITACAO_FLUXO = [
  Status.AGUARDANDO_DIGITACAO,
  Status.EM_DIGITACAO,
  Status.DIGITACAO_FINALIZADA,
];

const ADMIN_PIPELINE = [
  {
    key: 'CRIACAO',
    label: 'Criação / Análise',
    color: 'secondary',
    statuses: [Status.EM_CRIACAO, Status.EM_ANALISE],
  },
  {
    key: 'INSPECAO',
    label: 'Inspeção',
    color: 'info',
    statuses: INSPECAO_COMPLETA,
  },
  {
    key: 'DIGITACAO',
    label: 'Digitação',
    color: 'warning',
    statuses: DIGITACAO_FLUXO,
  },
  {
    key: 'GDIS',
    label: 'Executada GDIS',
    color: 'success',
    statuses: [Status.EXECUTADA_GDIS],
  },
  {
    key: 'NSIR_FINALIZADA',
    label: 'Finalizada',
    color: 'success',
    statuses: [Status.FINALIZADA],
  },
  {
    key: 'CANCELADA',
    label: 'Cancelado',
    color: 'error',
    statuses: [Status.CANCELADA],
  },
  {
    key: 'ERROS',
    label: 'Erros sync/envio',
    color: 'error',
    statuses: ERRO_INSPECAO,
  },
];

const INSPETOR_PIPELINE = [
  {
    key: 'AGUARDANDO',
    label: 'Aguardando Inspeção',
    color: 'info',
    statuses: [Status.AGUARDANDO_INSPECAO],
  },
  {
    key: 'EM_INSPECAO',
    label: 'Em Inspeção / Envio',
    color: 'warning',
    statuses: INSPECAO_ATIVA,
  },
  {
    key: 'FINALIZADA',
    label: 'Inspeção Finalizada',
    color: 'success',
    statuses: [Status.INSPECAO_FINALIZADA, Status.INSPECAO_ENVIADA_COM_SUCESSO],
  },
  {
    key: 'ERROS',
    label: 'Erros sync/envio',
    color: 'error',
    statuses: ERRO_INSPECAO,
  },
];

const DIGITADOR_PIPELINE = [
  {
    key: 'BUFFER',
    label: 'Inspeção Finalizada',
    color: 'info',
    statuses: [Status.INSPECAO_FINALIZADA, Status.INSPECAO_ENVIADA_COM_SUCESSO, Status.EM_ANALISE],
  },
  {
    key: 'AGUARDANDO',
    label: 'Aguardando Digitação',
    color: 'warning',
    statuses: [Status.AGUARDANDO_DIGITACAO],
  },
  {
    key: 'ANTECIPADA',
    label: 'Digitação antecipada',
    color: 'warning',
    statuses: INSPECTION_FIELD_STATUSES,
    digitacaoAntecipada: true,
  },
  {
    key: 'EM_DIGITACAO',
    label: 'Em Digitação',
    color: 'warning',
    statuses: [Status.EM_DIGITACAO],
  },
  {
    key: 'CONCLUIDAS',
    label: 'Finalizadas / GDIS',
    color: 'success',
    statuses: [Status.DIGITACAO_FINALIZADA, Status.EXECUTADA_GDIS],
  },
];

const USO_MUTUO_PIPELINE = [
  {
    key: 'AGUARDANDO',
    label: 'Aguardando Inspeção',
    color: 'info',
    statuses: [Status.AGUARDANDO_INSPECAO],
  },
  {
    key: 'EM_ANDAMENTO',
    label: 'Em Andamento',
    color: 'warning',
    statuses: INSPECAO_ATIVA,
  },
  {
    key: 'CONCLUIDAS',
    label: 'Finalizadas',
    color: 'success',
    statuses: [
      Status.INSPECAO_FINALIZADA,
      Status.INSPECAO_ENVIADA_COM_SUCESSO,
      ...DIGITACAO_FLUXO,
      Status.EXECUTADA_GDIS,
    ],
  },
];

const AUDITORIA_PIPELINE = [
  {
    key: 'DIGITACAO_FINALIZADA',
    label: 'Digitação Finalizada',
    color: 'warning',
    statuses: [Status.DIGITACAO_FINALIZADA],
  },
  {
    key: 'GDIS',
    label: 'Executada GDIS',
    color: 'success',
    statuses: [Status.EXECUTADA_GDIS],
  },
];

export const PIPELINE_BY_PERSONA = {
  [NSIR_PERSONA.ADMIN]: ADMIN_PIPELINE,
  [NSIR_PERSONA.DEFAULT]: ADMIN_PIPELINE,
  [NSIR_PERSONA.INSPETOR]: INSPETOR_PIPELINE,
  [NSIR_PERSONA.DIGITADOR]: DIGITADOR_PIPELINE,
  [NSIR_PERSONA.USO_MUTUO]: USO_MUTUO_PIPELINE,
  [NSIR_PERSONA.AUDITORIA_NSIR]: AUDITORIA_PIPELINE,
  [NSIR_PERSONA.CROQUI]: INSPETOR_PIPELINE,
};

export const resolveNsirListPersona = ({ authData, userRole }) => {
  const email = authData?.email;
  const role = userRole ?? authData?.role;

  if (email && isUsuarioUsoMuto(email)) return NSIR_PERSONA.USO_MUTUO;
  if (isRoleAuditoriaNsir(role)) return NSIR_PERSONA.AUDITORIA_NSIR;
  if (role === Role.DIGITADOR) return NSIR_PERSONA.DIGITADOR;
  if (role === Role.INSPETOR) return NSIR_PERSONA.INSPETOR;
  if (role === Role.CROQUI) return NSIR_PERSONA.CROQUI;
  if (role === Role.ADMIN || role === Role.SUPER_ADMIN) return NSIR_PERSONA.ADMIN;
  return NSIR_PERSONA.DEFAULT;
};

export const getPipelineGroupsForPersona = (persona) =>
  PIPELINE_BY_PERSONA[persona] ?? PIPELINE_BY_PERSONA[NSIR_PERSONA.DEFAULT];

export const getStatusesForPipelineGroup = (persona, groupKey) => {
  if (!groupKey) return null;
  const group = getPipelineGroupsForPersona(persona).find((item) => item.key === groupKey);
  return group?.statuses?.length ? group.statuses : null;
};

export const getPipelineGroupLabel = (persona, groupKey) => {
  if (!groupKey) return null;
  const group = getPipelineGroupsForPersona(persona).find((item) => item.key === groupKey);
  return group?.label ?? null;
};

export const sumStatusCounts = (counts, statuses) =>
  (statuses || []).reduce((acc, status) => acc + (Number(counts?.[status]) || 0), 0);

export const getGroupCardValues = (filtrado, total, group) => {
  if (group?.digitacaoAntecipada) {
    const filteredCount = Number(filtrado?.digitacaoAntecipadaCount);
    const totalCount = Number(total?.digitacaoAntecipadaCount);
    return {
      valueFiltered: Number.isFinite(filteredCount) ? filteredCount : 0,
      valueTotal: Number.isFinite(totalCount) ? totalCount : null,
    };
  }
  return {
    valueFiltered: sumStatusCounts(filtrado, group.statuses),
    valueTotal: Object.keys(total || {}).length > 0
      ? sumStatusCounts(total, group.statuses)
      : null,
  };
};

/** Vencimento: ADMIN, INSPETOR, DIGITADOR. Oculto: USO_MUTUO, AUDITORIA_NSIR, CROQUI, DEFAULT. */
export const showVencimentoStripForPersona = (persona) =>
  [NSIR_PERSONA.ADMIN, NSIR_PERSONA.INSPETOR, NSIR_PERSONA.DIGITADOR].includes(persona);

export const getPersonaSubtitle = (persona) => {
  switch (persona) {
    case NSIR_PERSONA.INSPETOR:
    case NSIR_PERSONA.DIGITADOR:
      return 'Suas NSIRs';
    case NSIR_PERSONA.AUDITORIA_NSIR:
      return 'Auditoria MT/BT';
    case NSIR_PERSONA.USO_MUTUO:
      return 'Inspeções Uso Mútuo';
    default:
      return null;
  }
};

export const getGridSizeForPipelineCount = (count) => {
  if (count <= 2) return { xs: 6, sm: 6 };
  if (count === 3) return { xs: 6, sm: 4 };
  if (count === 4) return { xs: 6, sm: 3 };
  return { xs: 6, sm: 4, md: 2.4 };
};
